import { mapValues, uniq } from 'es-toolkit';
import React, { useEffect, useMemo, useState } from 'react';

import { EstimateInfo, EstimateMetricsAndMissingPrices, ProjectEstimate } from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import { CreateEstimate, getDefaultEstimateInfo } from '@paragon/estimate-tools';
import {
  getComponentDesignQuantities,
  getFlattenedChildrenWithCalculatedQuantity,
  getLayoutModelsWithPendingChanges,
} from '@paragon/layout-models';
import { getLoggedInUser } from '@paragon/login-tools';
import { lumbersAreEqual } from '@paragon/material-lists';
import { closeCurrentPopup, Dialog } from '@paragon/popup-state';
import { createScrollbarStyling } from '@paragon/themes';

import { createEstimateCsv, createEstimatePdf } from '../../store/actions/estimateActions';
import { getDistanceFormatterSelector } from '../../store/selectors/getDistanceFormatterSelector';
import { getProjectLayoutModel } from '../../store/selectors/getProjectLayoutModel';
import { getComponentDesignQuantitiesForSharedComponentSet } from '../../store/selectors/sharedComponentSets';
import { useAppDispatch, useAppSelector } from '../hooks';

export function CreateEstimateDialog() {
  const dispatch = useAppDispatch();

  const loggedInUser = useAppSelector(getLoggedInUser);

  const openContext = useAppSelector((state) => state.application.openContext?.type);
  if (openContext !== 'project' && openContext !== 'shared-component-set')
    throw new Error('Opened create estimate dialog outside of project or shared component set context');

  const projectLayoutModel = useAppSelector(getProjectLayoutModel);
  const sharedComponentSet = useAppSelector((state) => state.sharedComponentSet.sharedComponentSet);
  const componentDesignQuantitiesForSharedComponentSet = useAppSelector(
    getComponentDesignQuantitiesForSharedComponentSet,
  );
  const layoutModelsByGuid = useAppSelector(getLayoutModelsWithPendingChanges);
  const projectFlattenedChildrenWithCalculatedQuantity = useAppSelector(getFlattenedChildrenWithCalculatedQuantity);
  const componentDesignQuantities =
    openContext === 'project' && projectLayoutModel
      ? getComponentDesignQuantities(projectFlattenedChildrenWithCalculatedQuantity(projectLayoutModel.guid))
      : mapValues(componentDesignQuantitiesForSharedComponentSet, (quantity) => quantity);
  const distanceFormatter = useAppSelector(getDistanceFormatterSelector);

  const estimateProjectName =
    openContext === 'project' && projectLayoutModel
      ? projectLayoutModel.name
      : (sharedComponentSet?.name ?? 'Estimate Project');
  const [estimateInfo, setEstimateInfo] = useState<EstimateInfo>(
    getDefaultEstimateInfo(estimateProjectName, loggedInUser?.email ?? 'Anonymous', componentDesignQuantities),
  );

  const [requireNonZeroPrices, setRequireNonZeroPrices] = useState<boolean>(false);
  const [submitAttempted, setSubmitAttempted] = useState<boolean>(false);
  const [existingProjectEstimates, setExistingProjectEstimates] = useState<
    readonly ProjectEstimate[] | 'error' | 'loading'
  >('loading');
  const [estimateMetricsAndMissingPrices, setEstimateMetricsAndMissingPrices] = useState<
    EstimateMetricsAndMissingPrices | 'loading' | 'error'
  >('loading');
  useEffect(() => {
    if (projectLayoutModel?.guid) {
      designService.estimates
        .getEstimatesForProject(projectLayoutModel.guid)
        .then((projectEstimates) => {
          setExistingProjectEstimates(projectEstimates);
        })
        .catch((error) => {
          setExistingProjectEstimates('error');
          console.error(error);
        });
    } else {
      setExistingProjectEstimates([]);
    }

    const estimateMetricsAndMissingPricesPromise =
      openContext === 'project' && projectLayoutModel
        ? designService.estimates.getEstimateMetricsAndMissingPricesForProject(projectLayoutModel.guid)
        : sharedComponentSet
          ? designService.estimates.getEstimateMetricsAndMissingPricesForSharedComponentSet(
              sharedComponentSet.lookupCode,
            )
          : null;
    if (estimateMetricsAndMissingPricesPromise === null) {
      setEstimateMetricsAndMissingPrices('error');
      return;
    }

    estimateMetricsAndMissingPricesPromise
      .then((estimateMetricsAndMissingPricesFromServer) => {
        setRequireNonZeroPrices(!!loggedInUser);
        setEstimateMetricsAndMissingPrices(estimateMetricsAndMissingPricesFromServer);
        setEstimateInfo((oldEstimateInfo) => ({
          ...oldEstimateInfo,
          userSpecifiedLumberPrices: estimateMetricsAndMissingPricesFromServer.missingLumberPrices.map(
            (missingLumberPrice) => ({
              ...missingLumberPrice,
              price: 0,
            }),
          ),
          userSpecifiedPlatePrices: Object.fromEntries(
            uniq(estimateMetricsAndMissingPricesFromServer.missingPlateTypePrices).map((plateType) => [plateType, 0]),
          ),
          userSpecifiedHangerPrices: Object.fromEntries(
            uniq(estimateMetricsAndMissingPricesFromServer.missingHangerPrices).map((hanger) => [hanger, 0]),
          ),
          userSpecifiedCustomHangerPrices: Object.fromEntries(
            uniq(estimateMetricsAndMissingPricesFromServer.missingCustomHangerPrices).map((hanger) => [hanger, 0]),
          ),
        }));
      })
      .catch((error) => {
        console.error(error);
        setEstimateMetricsAndMissingPrices('error');
        setRequireNonZeroPrices(false);
      });
    return () => setEstimateMetricsAndMissingPrices('loading');
  }, [projectLayoutModel, openContext, sharedComponentSet, loggedInUser]);

  const allNecessaryPricesSet = useMemo(() => {
    const anyPlatePriceMissing = Object.values(estimateInfo.userSpecifiedPlatePrices).some((price) => price === 0);
    if (anyPlatePriceMissing) return false;

    const anyHangerPriceMissing = estimateInfo.includeHangerPricing
      ? Object.values(estimateInfo.userSpecifiedHangerPrices).some((price) => price === 0) ||
        Object.values(estimateInfo.userSpecifiedCustomHangerPrices).some((price) => price === 0)
      : false;
    if (anyHangerPriceMissing) return false;

    for (const userSpecifiedLumberPrice of estimateInfo.userSpecifiedLumberPrices) {
      if (userSpecifiedLumberPrice.price !== 0) continue;
      if (
        !estimateInfo.userSpecifiedLumberPrices.some(
          (otherLumberPrice) =>
            lumbersAreEqual(otherLumberPrice.lumber, userSpecifiedLumberPrice.lumber) &&
            otherLumberPrice.stockLengthInInches > userSpecifiedLumberPrice.stockLengthInInches,
        )
      )
        return false;
    }

    return true;
  }, [estimateInfo]);

  return (
    <Dialog
      fullScreen
      title="Create Estimate"
      onCancel="close"
      submitText="Create Estimate PDF"
      onSubmit={() => {
        if (requireNonZeroPrices && !allNecessaryPricesSet) {
          setSubmitAttempted(true);
          return;
        }
        dispatch(createEstimatePdf(estimateInfo));
        dispatch(closeCurrentPopup());
      }}
      otherButtons={{
        'Create Estimate CSV': {
          handler: () => {
            if (requireNonZeroPrices && !allNecessaryPricesSet) {
              setSubmitAttempted(true);
              return;
            }
            dispatch(createEstimateCsv(estimateInfo));
            dispatch(closeCurrentPopup());
          },
          containedVariant: true,
        },
      }}
      contentSx={createScrollbarStyling}
    >
      <CreateEstimate
        estimateInfo={estimateInfo}
        setEstimateInfo={setEstimateInfo}
        existingProjectEstimates={existingProjectEstimates}
        estimateMetricsAndMissingPrices={estimateMetricsAndMissingPrices}
        requireNonZeroPrices={requireNonZeroPrices}
        setRequireNonZeroPrices={setRequireNonZeroPrices}
        distanceFormatter={distanceFormatter}
        componentDesignQuantities={componentDesignQuantities}
        layoutModelsByGuid={layoutModelsByGuid}
        submitAttemptedWithoutAllPrices={submitAttempted && !allNecessaryPricesSet}
      />
    </Dialog>
  );
}
